import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Edit3, X, Image as ImageIcon, ZoomIn } from 'lucide-react';
import { ParsedQuestion } from './types';

interface InlineQuestionPreviewProps {
  questions: ParsedQuestion[];
  onEdit: (index: number) => void;
  onRemoveImage: (questionIndex: number, imageIndex: number) => void;
  imageUrls: Map<number, string[]>;
}

interface ImageModalProps {
  imageUrl: string;
  description?: string;
  onClose: () => void;
}

const ImageModal: React.FC<ImageModalProps> = ({ imageUrl, description, onClose }) => {
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative max-w-4xl max-h-[90vh] bg-white rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-2 right-2 bg-white rounded-full p-2 hover:bg-gray-100 transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>
        <img
          src={imageUrl}
          alt="Full size"
          className="max-w-full max-h-[80vh] object-contain"
        />
        {description && (
          <div className="p-4 bg-gray-50 border-t">
            <p className="text-sm text-gray-700">
              <span className="font-medium">AI Description:</span> {description}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const API_URL = import.meta.env.VITE_API_URL || '';

const InlineQuestionPreview: React.FC<InlineQuestionPreviewProps> = ({
  questions,
  onEdit,
  onRemoveImage,
  imageUrls
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [viewingImage, setViewingImage] = useState<{url: string, description?: string} | null>(null);

  const getQuestionTypeDisplay = (q: ParsedQuestion) => {
    let typeDisplay = q.type;
    
    // Add scale information for scale-based questions
    if (['rating_scale', 'likert_scale', 'nps'].includes(q.type)) {
      const min = q.scale_min || 1;
      const max = q.scale_max || (q.type === 'nps' ? 10 : 5);
      
      if (q.type === 'likert_scale') {
        typeDisplay = `Likert Scale (${min}-${max})`;
      } else if (q.type === 'rating_scale') {
        typeDisplay = `Rating Scale (${min}-${max})`;
      } else if (q.type === 'nps') {
        typeDisplay = `NPS (${min}-${max})`;
      }
    }
    
    // Add choice count for choice-based questions
    if (q.choices && q.choices.length > 0) {
      if (q.type === 'single_choice') {
        typeDisplay = `Single Choice (${q.choices.length} options)`;
      } else if (q.type === 'multiple_choice') {
        const maxSelect = q.max_selections ? ` - select up to ${q.max_selections}` : '';
        typeDisplay = `Multiple Choice (${q.choices.length} options${maxSelect})`;
      } else if (q.type === 'ranking') {
        const rankCount = q.ranking_count ? ` - rank top ${q.ranking_count}` : '';
        typeDisplay = `Ranking (${q.choices.length} options${rankCount})`;
      }
    }
    
    // MaxDiff display
    if (q.type === 'max_diff' && q.maxdiff_items) {
      const setsPerPersona = Math.max(3, Math.ceil(q.maxdiff_items.length / 4));
      typeDisplay = `MaxDiff (${q.maxdiff_items.length} items, ${setsPerPersona} sets)`;
    }
    
    // Conjoint display
    if (q.type === 'conjoint_analysis' && q.conjoint_attributes) {
      typeDisplay = `Conjoint (${q.conjoint_attributes.length} attributes)`;
    }
    
    return typeDisplay;
  };

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">
          Parsed Questions ({questions.length})
        </h3>
      </div>

      {questions.map((question, index) => {
        const isExpanded = expandedIndex === index;
        const localImageUrls = imageUrls.get(index) || [];
        const backendImagePaths = question.image_paths || [];
        
        // Use backend paths if available (after parsing), otherwise use local URLs
        const displayImageUrls = backendImagePaths.length > 0 ? backendImagePaths : localImageUrls;
        const hasImages = displayImageUrls.length > 0;
        const imageCount = displayImageUrls.length;

        return (
          <div 
            key={question.id || index}
            className="border border-green-200 rounded-lg bg-green-50 overflow-hidden"
          >
            {/* Header */}
            <div className="p-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-xs font-semibold text-green-700">
                      Q{index + 1}
                    </span>
                    <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded">
                      {getQuestionTypeDisplay(question)}
                    </span>
                    {hasImages && (
                      <span className="flex items-center text-xs text-green-600">
                        <ImageIcon className="w-3 h-3 mr-1" />
                        {imageCount} {imageCount === 1 ? 'Image' : 'Images'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-green-800 font-medium">
                    {question.text}
                  </p>
                </div>
                <div className="flex items-center space-x-1 ml-2">
                  <button
                    onClick={() => onEdit(index)}
                    className="text-green-600 hover:text-green-700 p-1"
                    title="Edit question"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => toggleExpand(index)}
                    className="text-green-600 hover:text-green-700 p-1"
                    title={isExpanded ? "Collapse" : "Expand"}
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Image thumbnails if exist and attached at question level */}
              {hasImages && displayImageUrls.length > 0 && question.image_attachment_level !== 'option' && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {displayImageUrls.map((imgUrl, imgIndex) => {
                    const fullImageUrl = imgUrl.startsWith('http') ? imgUrl : `${API_URL}${imgUrl}`;
                    const imageDescription = question.image_descriptions?.[imgIndex];
                    
                    return (
                      <div key={imgIndex} className="relative inline-block group">
                        <img
                          src={fullImageUrl}
                          alt={`Question ${index + 1} - Image ${imgIndex + 1}`}
                          className="h-16 w-16 object-cover rounded border border-green-300 cursor-pointer hover:border-green-500 transition-colors"
                          onClick={() => setViewingImage({url: fullImageUrl, description: imageDescription})}
                        />
                        {/* Zoom icon overlay */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <ZoomIn className="w-5 h-5 text-white" />
                        </div>
                        {/* Remove button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveImage(index, imgIndex);
                          }}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors z-10"
                          title="Remove image"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Show attachment level indicator */}
              {hasImages && (
                <div className="mt-1">
                  <span className="text-[10px] text-green-600 bg-green-100 px-1.5 py-0.5 rounded">
                    {question.image_attachment_level === 'option' ? '📷 Images as options' : '📷 Images provide context'}
                  </span>
                </div>
              )}
            </div>

            {/* Expanded details */}
            {isExpanded && (
              <div className="border-t border-green-200 bg-white p-3">
                {/* Show choices for choice-based questions */}
                {question.choices && question.choices.length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs font-medium text-gray-700 mb-1">Options:</p>
                    {question.image_attachment_level === 'option' && question.option_images ? (
                      // Render options with images - horizontal display with numbered badges
                      <div className="flex flex-wrap gap-2">
                        {question.choices.map((choice, i) => {
                          const imageUrl = question.option_images?.[choice];
                          const fullImageUrl = imageUrl ? (imageUrl.startsWith('http') ? imageUrl : `${API_URL}${imageUrl}`) : null;
                          
                          return (
                            <div key={i} className="relative">
                              {/* Number badge on top-left */}
                              <div className="absolute top-1 left-1 bg-gray-900 bg-opacity-75 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center z-10">
                                {i + 1}
                              </div>
                              {fullImageUrl ? (
                                <img
                                  src={fullImageUrl}
                                  alt={`Option ${i + 1}`}
                                  className="h-24 w-24 object-cover rounded border-2 border-gray-300 cursor-pointer hover:border-blue-400"
                                  onClick={() => setViewingImage({url: fullImageUrl})}
                                />
                              ) : (
                                <div className="h-24 w-24 flex items-center justify-center bg-gray-100 rounded border-2 border-gray-300">
                                  <span className="text-xs text-gray-400">N/A</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      // Regular text options
                      <ul className="list-disc list-inside space-y-1">
                        {question.choices.map((choice, i) => (
                          <li key={i} className="text-xs text-gray-600">
                            {choice}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Show scale info for rating questions */}
                {['rating_scale', 'likert_scale', 'nps'].includes(question.type) && (
                  <div className="mb-2">
                    <p className="text-xs text-gray-600">
                      Scale: {question.scale_min || 1} to {question.scale_max || (question.type === 'nps' ? 10 : 5)}
                    </p>
                  </div>
                )}

                {/* Show ranking info */}
                {question.type === 'ranking' && question.ranking_count && (
                  <div className="mb-2">
                    <p className="text-xs text-gray-600">
                      Rank top {question.ranking_count} options
                    </p>
                  </div>
                )}

                {/* Show MaxDiff items */}
                {question.type === 'max_diff' && question.maxdiff_items && question.maxdiff_items.length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs font-medium text-gray-700 mb-1">
                      MaxDiff Items ({question.maxdiff_items.length} items, 4 per set, {Math.max(3, Math.ceil(question.maxdiff_items.length / 4))} sets per persona):
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {question.maxdiff_items.map((item, i) => (
                        <span
                          key={i}
                          className="px-2 py-1 bg-white text-gray-700 border border-gray-300 rounded text-xs"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show Conjoint attributes */}
                {question.type === 'conjoint_analysis' && question.conjoint_attributes && question.conjoint_attributes.length > 0 && (
                  <div className="mb-2">
                    <p className="text-xs font-medium text-gray-700 mb-1">Conjoint Attributes:</p>
                    <div className="space-y-2">
                      {question.conjoint_attributes.map((attr, i) => (
                        <div key={i} className="bg-white p-2 rounded border border-gray-300">
                          <div className="text-xs font-medium text-gray-800 mb-1">
                            {attr.name}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {attr.levels.map((level, j) => (
                              <span
                                key={j}
                                className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded text-xs"
                              >
                                {level}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {question.conjoint_description && (
                      <p className="text-xs text-gray-600 italic mt-2">
                        Task: {question.conjoint_description}
                      </p>
                    )}
                  </div>
                )}

                {/* Show larger image previews in expanded view */}
                {hasImages && displayImageUrls.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-gray-700 mb-2">Images for this question:</p>
                    <div className="grid grid-cols-2 gap-3">
                      {displayImageUrls.map((imgUrl, imgIndex) => {
                        const fullImageUrl = imgUrl.startsWith('http') ? imgUrl : `${API_URL}${imgUrl}`;
                        const imageDescription = question.image_descriptions?.[imgIndex];
                        
                        return (
                          <div key={imgIndex} className="space-y-1">
                            <div className="relative group cursor-pointer" onClick={() => setViewingImage({url: fullImageUrl, description: imageDescription})}>
                              <img
                                src={fullImageUrl}
                                alt={`Question ${index + 1} - Image ${imgIndex + 1}`}
                                className="w-full h-32 object-cover rounded border border-gray-300 hover:border-blue-400 transition-colors"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded flex items-center justify-center opacity-0 group-hover:opacity-100">
                                <div className="bg-white rounded-full p-2">
                                  <ZoomIn className="w-5 h-5 text-gray-700" />
                                </div>
                              </div>
                            </div>
                            {imageDescription && (
                              <p className="text-xs text-gray-600 italic">
                                {imageDescription}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Image Modal for full-size view */}
      {viewingImage && (
        <ImageModal
          imageUrl={viewingImage.url}
          description={viewingImage.description}
          onClose={() => setViewingImage(null)}
        />
      )}
    </div>
  );
};

export default InlineQuestionPreview;

